import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { generateOptionItems, optionMapToOptions } from 'components/OfferList/OfferListFilter/OfferListFilterUtils'
import useToggle from 'hooks/useToggle'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Modal from 'LuxKit/Modal/Modal'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import LayoutContainer from 'components/Common/LayoutContainer'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'

const CategoryContainer = styled.div`
  margin-top: ${rem(16)};
`

interface Props {
  availableFilters: Record<string, Record<string, number>> | undefined
  label: string
  selectedFilters: Array<string>
  search: string
  searchQueryKey: string
  place?: App.Place
  onFilterChange: FilterControlChangeHandler
  largeFilterChipSize?: boolean
}

function InclusionsDropdown(props: Props) {
  const { availableFilters, selectedFilters, label, search, searchQueryKey, onFilterChange, largeFilterChipSize = false } = props
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const [isDropdownOpen, toggleDropDown, , closeDropdown] = useToggle()
  const [localSelectedFilters, setLocalSelectedFilters] = useState<Set<string>>(new Set(selectedFilters))
  const filterChipRef = useRef<HTMLButtonElement>(null)
  const inclusionItems = useMemo(() => {
    const inclusions = Object.entries(availableFilters ?? {}).reduce((acc, [category, inclusions]) => {
      acc[category] = optionMapToOptions(inclusions ?? {}, {})
      return acc
    }, {} as Record<string, Array<[string, number, number]>>)

    return Object.entries(inclusions).reduce((acc, [category, inclusionOptions]) => {
      acc[category] = generateOptionItems(inclusionOptions, new Set(localSelectedFilters))
      return acc
    }, {} as Record<string, Array<FilterPanelCheckItem>>)
  }, [availableFilters, localSelectedFilters])

  const onFilterTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSelectedFilters((prev) => {
      const newSet = new Set(prev)
      if (e.target.checked) {
        newSet.add(e.target.value)
      } else {
        newSet.delete(e.target.value)
      }
      return newSet
    })
  }, [])

  const onApply = useCallback(() => {
    onFilterChange({ search: setSearchParamValue(search, searchQueryKey, Array.from(localSelectedFilters).join(',')) })
    closeDropdown()
  }, [closeDropdown, localSelectedFilters, onFilterChange, search, searchQueryKey])

  const onClose = useCallback(() => {
    onApply()
    closeDropdown()
  }, [closeDropdown, onApply])

  const onReset = useCallback(() => {
    setLocalSelectedFilters(new Set())
  }, [])

  useEffect(() => {
    setLocalSelectedFilters(new Set(selectedFilters))
  }, [selectedFilters])

  const hasAvailableFilters = Object.entries(availableFilters ?? {}).length > 0

  const resetText = intl.formatMessage({
    id: 'RAHy2iev_xzNi9PbHiRO',
    defaultMessage: 'Reset',
    description: 'Button text to reset inclusions filter in the search or listing filters panel',
  })

  const applyText = intl.formatMessage({
    id: 'rooDZXgr7NIYL_4PKy8C',
    defaultMessage: 'Apply',
    description: 'Button text to apply inclusions filter in the search or listing filters panel',
  })

  const noAvailableFiltersText = intl.formatMessage({
    id: '35rJ0F53OKmeRrPwsl7g',
    defaultMessage: 'No available filters',
    description: 'Message shown when no inclusion filters are available',
  })

  const resetAllText = intl.formatMessage({
    id: 'w2g9RLcGZU97OgZAHw8U',
    defaultMessage: 'Reset all',
    description: 'Button text to reset all selected inclusions in the inclusions list',
  })

  const formatLabelWithCount = useCallback(
    (baseLabel: string) => {
      return localSelectedFilters.size ?
          intl.formatMessage(
            {
              id: '5HtI1Lmi-09QyeiNtM70',
              defaultMessage: '{label} ({count})',
              description:
              'Filter label showing the base text followed by the selected inclusion count in hotel search',
            },
            {
              label: baseLabel,
              count: localSelectedFilters.size,
            },
          ) :
        baseLabel
    },
    [intl, localSelectedFilters.size],
  )

  const resetLabel = formatLabelWithCount(resetAllText)
  const chipLabel = formatLabelWithCount(label)

  return (
    <div>
      <FilterChip selected={!!localSelectedFilters.size} ref={filterChipRef} size={largeFilterChipSize ? 'large' : 'medium'} onClick={toggleDropDown} endIcon={isDropdownOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}>
        {chipLabel}
      </FilterChip>
      <CSSBreakpoint min="tablet">
        <DropdownList
          size="M"
          triggerRef={filterChipRef}
          anchorRef={filterChipRef}
          open={isDropdownOpen}
          placement="bottom-start"
          onClose={onClose}
          secondaryActionProps={{
            kind: 'tertiary',
            children: resetText,
            onClick: onReset,
          }}
          primaryActionProps={{
            children: applyText,
            onClick: onApply,
          }}
        >
          <LayoutContainer>
            <FilterPanelFilterGroup allowToggle={false} showDropdown={false} title={label} showBorder={false}>
              {!hasAvailableFilters && <BodyText variant="medium">{noAvailableFiltersText}</BodyText>}
              {hasAvailableFilters && Object.entries(inclusionItems).map(([category, items]) => (
                <CategoryContainer key={category}>
                  <BodyText variant="medium" weight="bold">{category}</BodyText>
                  <FilterPanelCheckboxGroup
                    key={category}
                    name="inclusions"
                    items={items}
                    onChange={onFilterTypeChange}
                    maxItems={5}
                    allowShowMore={false}
                  />
                </CategoryContainer>
              ))}
            </FilterPanelFilterGroup>
          </LayoutContainer>
        </DropdownList>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <Modal
          title={label}
          isOpen={isDropdownOpen && isMobile}
          onClose={closeDropdown}
          primaryActionText={applyText}
          onPrimaryActionClick={onClose}
          secondaryActionText={resetLabel}
          onSecondaryActionClick={onReset}
        >
          {!hasAvailableFilters && <BodyText variant="medium">{noAvailableFiltersText}</BodyText>}
          {hasAvailableFilters && Object.entries(inclusionItems).map(([category, items]) => (
            <CategoryContainer key={category}>
              <BodyText variant="medium" weight="bold">{category}</BodyText>
              <FilterPanelCheckboxGroup
                key={category}
                name="inclusions"
                items={items}
                onChange={onFilterTypeChange}
                maxItems={5}
                allowShowMore={false}
              />
            </CategoryContainer>
          ))}
        </Modal>
      </CSSBreakpoint>
    </div>
  )
}

export default InclusionsDropdown
